/**
 * Client mirror of config/companyRoles.js.
 *
 * Written / API values: enterprise_admin | enterprise_user | enterprise_manager.
 * Short values (admin, member, manager, owner) are accepted on read.
 * "Owner" is a display label from Enterprise.ownerId (`isOwner`), not a stored role.
 */

export const ENTERPRISE_ADMIN = 'enterprise_admin';
export const ENTERPRISE_USER = 'enterprise_user';
export const ENTERPRISE_MANAGER = 'enterprise_manager';

/** @deprecated short names — accepted on read only */
export const OWNER = 'owner';
export const ADMIN = ENTERPRISE_ADMIN;
export const MEMBER = ENTERPRISE_USER;
export const MANAGER = ENTERPRISE_MANAGER;

const SHORT_ALIASES = {
  admin: ENTERPRISE_ADMIN,
  member: ENTERPRISE_USER,
  user: ENTERPRISE_USER,
  manager: ENTERPRISE_MANAGER,
  owner: ENTERPRISE_ADMIN,
};

export function canonicalRole(role) {
  if (!role) return null;
  const value = String(role);
  if ([ENTERPRISE_ADMIN, ENTERPRISE_USER, ENTERPRISE_MANAGER].includes(value)) return value;
  return SHORT_ALIASES[value] || null;
}

/** The written role from an API row, whichever field it arrived in. */
export function roleOf(row) {
  return canonicalRole(row?.companyRole) || canonicalRole(row?.role);
}

export function isAdminRole(role) {
  return canonicalRole(role) === ENTERPRISE_ADMIN;
}

export function isCompanyAdmin(row) {
  return isAdminRole(roleOf(row)) || !!row?.isOwner;
}

/**
 * Display label. Pass the API row so Owner is shown from `isOwner`, not from a
 * stored role that can disagree with Enterprise.ownerId.
 */
export function roleLabel(roleOrRow, extras = {}) {
  const row = roleOrRow && typeof roleOrRow === 'object' ? roleOrRow : null;
  const isOwner = extras.isOwner ?? !!row?.isOwner;
  const role = row ? roleOf(row) : canonicalRole(roleOrRow);
  if (isOwner) return 'Owner';
  switch (role) {
    case ENTERPRISE_ADMIN:
      return 'Admin';
    case ENTERPRISE_MANAGER:
      return 'Manager';
    case ENTERPRISE_USER:
      return 'Member';
    default:
      return '—';
  }
}
